import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import AddDesignButton from './OrderWizard/AddDesignButton';
import AddProductButton from './OrderWizard/AddProductButton';
import Modal from './OrderWizard/Modal';
import snakecase from 'lodash.snakecase';
import reCAPTCHABase from '../reCAPTCHABase';

const getStoredFormData = () => {
  try {
    const storedData = localStorage.getItem('formData');
    const parsed = JSON.parse(storedData) || {
      isStudent: true,
      school: 'Other',
    };
    return parsed;
  } catch (e) {
    return {
      isStudent: true,
      school: 'Other',
    };
  }
};

const storedStepNumber = () => {
  const saved = localStorage.getItem('modalStepNumber');
  return saved ? parseInt(saved, 10) : 1;
};

const openModalButton = ({ isNew, onClick }) => {
  return (
    <button
      className={`button wizard-open-button mlm ${
        isNew ? '' : 'wizard-open-button--animation'
      }`}
      type="button"
      onClick={onClick}
    >
      Design Studio
    </button>
  );
};

const OrderWizard = props => {
  const addDesignDomNode = useMemo(
    () => document.querySelector('#add-design-button'),
    []
  );
  const addProductDomNode = useMemo(
    () => document.querySelector('#add-product-button'),
    []
  );

  const desktopWizardButtonDomNode = useMemo(
    () => document.getElementById('desktop-wizard-button'),
    []
  );

  const mobileWizardButtonDomNode = useMemo(
    () => document.getElementById('mobile-wizard-button'),
    []
  );

  const quotePageButtonDomNode = useMemo(
    () => document.getElementById('quote-page-button'),
    []
  );

  const [formData, setFormDataRaw] = useState(getStoredFormData());
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalStepNumber, setModalStepNumber] = useState(storedStepNumber());
  const [previewUrl, setPreviewUrl] = useState(null);

  // Wrap setFormData to debug what's changing the color
  const setFormData = newData => {
    if (typeof newData === 'function') {
      setFormDataRaw(prevData => {
        const nextData = newData(prevData);
        if (
          prevData.productColor &&
          nextData.productColor &&
          prevData.productColor.id !== nextData.productColor.id
        ) {
        }
        return nextData;
      });
    } else {
      setFormDataRaw(newData);
    }
  };

  // Use a ref to always have access to the latest formData
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  useEffect(() => {
    // Set flag and dispatch event to signal OrderWizard is ready
    window.OrderWizardReady = true;
    window.dispatchEvent(new CustomEvent('OrderWizardReady'));

    // Listen for designs from the gallery
    const handleLoadDesignFromGallery = event => {
      const designData = event.detail;
      if (designData && designData.fromGallery) {
        // Reload form data from localStorage to get the latest state
        const updatedFormData = getStoredFormData();
        setFormData(updatedFormData);

        // Only open modal if requested (first design/product)
        if (designData.openModal) {
          // If we have both product and design, jump to customization
          // Otherwise start at page 1
          setModalStepNumber(1);
          setModalIsOpen(true);
          document.body.classList.add('wizard-no-scroll');
        }
      }
    };

    // Listen for storage events to update when localStorage changes
    const handleStorageChange = e => {
      // Handle both real storage events (from other tabs) and fake ones (from same tab)
      if (
        e &&
        e.storageArea === localStorage &&
        e.key === 'formData' &&
        e.oldValue !== null
      ) {
        // Real storage event from another tab
        const updatedFormData = getStoredFormData();
        setFormData(updatedFormData);
      } else if (e && !e.storageArea) {
        // Fake storage event dispatched from same page (DesignGalleryIntegration)
        const updatedFormData = getStoredFormData();
        setFormData(updatedFormData);
      }
    };

    // Listen for event to open modal
    const handleOpenModalEvent = event => {
      // Reload formData from localStorage to ensure we have the latest state
      const latestFormData = getStoredFormData();
      setFormData(latestFormData);

      // If no modalStepNumber in localStorage, ensure we start from the beginning
      const savedStep = localStorage.getItem('modalStepNumber');
      if (!savedStep) {
        setModalStepNumber(1);
      }

      setModalIsOpen(true);
      document.body.classList.add('wizard-no-scroll');
    };

    window.addEventListener(
      'LoadDesignFromGallery',
      handleLoadDesignFromGallery
    );
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('OpenOrderWizardModal', handleOpenModalEvent);

    return () => {
      window.removeEventListener(
        'LoadDesignFromGallery',
        handleLoadDesignFromGallery
      );
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('OpenOrderWizardModal', handleOpenModalEvent);
    };
  }, []);

  const designRequestInProgress = formData.design || formData.product;

  const clearLocalStorage = () => {
    setFormData({
      isStudent: formData.isStudent,
      school: 'Other',
      designCustomization: null,
      designNotes: null
    });
    setModalStepNumber(1);
    setPreviewUrl(null);
    localStorage.removeItem('modalStepNumber');
    localStorage.removeItem('formData');
    localStorage.removeItem('quoteJustSubmitted');
  };

  function handleStartOver() {
    const userConfirmed = window.confirm(
      'Are you sure you want to start over? This will clear all of your selections and inputted data.'
    );
    if (userConfirmed === true) {
      clearLocalStorage();
      localStorage.removeItem('quoteJustSubmitted'); // Clear this flag when starting over
    }
  }

  function handleRemoveDesign() {
    if (
      window.confirm(
        'Are you sure you want to remove your design? This will remove your design from your quote request.'
      ) === true
    ) {
      handleChange('design', null);
    }
  }

  function handleRemoveProduct() {
    if (
      window.confirm(
        'Are you sure you want to remove your product? This will remove your product from your quote request.'
      ) === true
    ) {
      handleChange('product', null);
      handleChange('productColor', null);
    }
  }

  function handleChange(name, value) {
    // Special handling for designCustomization to prevent overwriting product data
    if (name === 'designCustomization') {
      console.log('[OrderWizard] handleChange designCustomization, designPool:', value && value.designPool);
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: value,
        // Preserve the current product data when updating designCustomization
        product: prevFormData.product,
        productColor: prevFormData.productColor,
      }));
    } else {
      // Use functional update to ensure we're always working with the latest state
      setFormData(prevFormData => ({ ...prevFormData, [name]: value }));
    }
  }

  function updateFormDataAttributes(attributes) {
    setFormData({ ...formData, ...attributes });
  }

  function updateDesign(design) {
    handleChange('design', {
      id: design.id,
      url: design.url,
      artCode: design.artCode,
      designUrl: design.designUrl,
    });
    // If we have a product already, go to step 2, otherwise step 1
    if (formData.product) {
      setModalStepNumber(2);
    } else {
      setModalStepNumber(1);
    }
    setModalIsOpen(true);
  }

  function updateProduct(product, productColor) {
    // If we're updating the same product and no new color is provided, keep the existing color
    const shouldKeepExistingColor =
      formData.product &&
      formData.product.id === product.id &&
      !productColor &&
      formData.productColor &&
      formData.productColor.id !== 'default';

    // Ensure productColor has all required properties
    const safeProductColor = shouldKeepExistingColor
      ? formData.productColor
      : productColor || {};

    const newProductColor = {
      id: safeProductColor.id || 'default',
      name: safeProductColor.name || 'Default',
      marketingImageUrl:
        safeProductColor.marketingImageUrl ||
        safeProductColor.marketing_image_url ||
        '',
      marketing_image_url:
        safeProductColor.marketingImageUrl ||
        safeProductColor.marketing_image_url ||
        '',
      hexCode:
        safeProductColor.hexCode || safeProductColor.hex_code || '#FFFFFF',
      hex_code:
        safeProductColor.hexCode || safeProductColor.hex_code || '#FFFFFF',
    };

    const newAttributes = {
      product: {
        id: product.id,
        description: product.description,
        name: product.name,
        style: product.style,
        brandName: product.brandName,
        productUrl: product.productUrl,
      },
      productColor: newProductColor,
    };

    updateFormDataAttributes(newAttributes);

    const currentFormData = { ...formData, ...newAttributes };

    try {
      localStorage.setItem('formData', JSON.stringify(currentFormData));
    } catch (e) {}

    setModalIsOpen(true);
  }

  const snakeCaseKeys = obj => {
    if (Array.isArray(obj)) {
      return obj.map(snakeCaseKeys);
    } else if (obj !== null && typeof obj === 'object') {
      return Object.keys(obj).reduce((acc, key) => {
        const snakeKey = snakecase(key);
        acc[snakeKey] = snakeCaseKeys(obj[key]);
        return acc;
      }, {});
    }
    return obj;
  };

  const handleSubmitQuoteRequest = () => {
    // Mark that a quote has been submitted to prevent drawer from auto-opening
    localStorage.setItem('quoteJustSubmitted', 'true');

    // Reload formData from localStorage to ensure we have the latest design customization
    const latestFormData = getStoredFormData();
    setFormData(latestFormData);

    // When submitting, use the latest formData from localStorage (includes all design pool updates)
    const fullFormData = { ...latestFormData };

    // Optimize payload: Remove large SVG content and unnecessary data
    if (fullFormData.designCustomization) {
      const cleanedCustomization = { ...fullFormData.designCustomization };
      // Remove SVG content that's too large and can be refetched
      delete cleanedCustomization.sideSvgContent;
      delete cleanedCustomization.side_svg_content;
      delete cleanedCustomization.designPoolSvgContent;
      delete cleanedCustomization.design_pool_svg_content;
      delete cleanedCustomization.pngDesigns; // Remove PNG data if present

      // Optimize design pool - only send essential data
      if (
        cleanedCustomization.designPool &&
        cleanedCustomization.designPool.length > 0
      ) {
        cleanedCustomization.designPool = cleanedCustomization.designPool.map(
          d => ({
            id: d.id,
            artCode: d.artCode,
            url: d.url || d.thumbnailUrl,
          })
        );
      }

      // Optimize sideDesigns - remove redundant data
      if (cleanedCustomization.sideDesigns) {
        const optimizedSideDesigns = {};
        Object.entries(cleanedCustomization.sideDesigns).forEach(
          ([side, designs]) => {
            if (designs && designs.length > 0) {
              optimizedSideDesigns[side] = designs.map(d => ({
                id: d.id,
                artCode: d.artCode,
                url: d.url || d.thumbnailUrl,
              }));
            }
          }
        );
        cleanedCustomization.sideDesigns = optimizedSideDesigns;
      }

      fullFormData.designCustomization = cleanedCustomization;
    }

    const quote_form = snakeCaseKeys(fullFormData);
    const payload = { quote_form: quote_form };

    reCAPTCHABase('quote_form')
      .then(p => {
        const finalPayload = { ...payload, ...p };

        return fetch('/quote', {
          credentials: 'same-origin',
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify(finalPayload),
        });
      })
      .then(response => {
        // Parse JSON response first to get error details
        return response.json().then(data => {
          if (!response.ok) {
            // If server returned an error, throw with the error message
            if (data.message) {
              throw new Error(data.message);
            }
            throw new Error(`Server returned ${response.status}`);
          }
          return data;
        });
      })
      .then(data => {
        // Store the preview URL from the response
        if (data.preview_url) {
          setPreviewUrl(data.preview_url);
        }
        setModalStepNumber(3);
        // Don't clear data here - page 3 needs it to display the thank you page
        // Data will be cleared when modal is closed from page 3
      })
      .catch(error => {
        // Handle quote submission error with specific message
        const errorMessage =
          error.message ||
          'There was an error submitting your quote request. Please try again.';
        alert(errorMessage);
        console.error('Quote submission error:', error);
      });
  };

  useEffect(() => {
    // Skip the initial save on mount to prevent saving stale data
    const timeoutId = setTimeout(() => {
      // Save to localStorage when browser is idle to avoid blocking UI
      const saveToStorage = () => {
        // Use the ref to get the latest formData
        const currentFormData = formDataRef.current;

        // Create a shallow copy for localStorage
        const dataToStore = { ...currentFormData };

        // Handle designCustomization separately to avoid deep cloning everything
        if (dataToStore.designCustomization) {
          // Create a shallow copy of designCustomization
          const customizationCopy = { ...dataToStore.designCustomization };

          // Remove large SVG content fields to avoid quota issues
          delete customizationCopy.sideSvgContent;
          delete customizationCopy.side_svg_content;
          delete customizationCopy.designPoolSvgContent;
          delete customizationCopy.design_pool_svg_content;
          delete customizationCopy.sideSvgContent;
          delete customizationCopy.designPoolSvgContent;

          dataToStore.designCustomization = customizationCopy;
        }

        try {
          localStorage.setItem('formData', JSON.stringify(dataToStore));
        } catch (e) {
          if (e.name === 'QuotaExceededError') {
            // Try to clear old data and retry
            localStorage.removeItem('selectedDesigns');
            localStorage.removeItem('targetDesignSide');
            localStorage.removeItem('existingDesigns');

            try {
              localStorage.setItem('formData', JSON.stringify(dataToStore));
            } catch (retryError) {
              console.error('Storage quota exceeded even after cleanup');
              // Don't show alert here as it might interrupt user workflow
              // The data is still in memory and will be submitted with the form
            }
          }
        }
      };

      // Use requestIdleCallback if available for better performance
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(saveToStorage, { timeout: 1000 });
      } else {
        saveToStorage();
      }
    }, 800); // Increased delay to 800ms to reduce localStorage writes during typing

    return () => clearTimeout(timeoutId);
  }, [formData]);

  useEffect(() => {
    localStorage.setItem('modalStepNumber', modalStepNumber.toString());
  }, [modalStepNumber]);

  function handleOpenModal() {
    // Only clear the quote submitted flag if we're starting fresh (no saved step or step 1)
    const savedStep = localStorage.getItem('modalStepNumber');
    if (!savedStep || savedStep === '1') {
      localStorage.removeItem('quoteJustSubmitted');
    }

    // Reload formData from localStorage to ensure we have the latest state
    const latestFormData = getStoredFormData();
    setFormData(latestFormData);

    // If no modalStepNumber in localStorage, ensure we start from the beginning
    if (!savedStep) {
      setModalStepNumber(1);
    }

    setModalIsOpen(true);
    document.body.classList.add('wizard-no-scroll');
  }

  function closeModal() {
    setModalIsOpen(false);
    if (modalStepNumber >= 3) {
      // Clear everything when closing from the thank you page
      clearLocalStorage();
    }
    document.body.classList.remove('wizard-no-scroll');
  }

  // Get current product from page
  const currentProduct =
    addProductDomNode && addProductDomNode.dataset.product
      ? JSON.parse(addProductDomNode.dataset.product)
      : null;

  // Determine button text based on state
  let addProductButtonText;
  if (formData && formData.product) {
    // Check if the current page product matches the stored product (ignoring color)
    if (currentProduct && currentProduct.id === formData.product.id) {
      addProductButtonText = 'Added to Design Studio';
    } else {
      addProductButtonText = 'Replace product in Design Studio';
    }
  } else {
    addProductButtonText = 'Add to Design Studio';
  }

  const addDesignButtonText = 'Add this design to the Design Studio';

  return (
    <>
      <div
        id="wizard-bg-overlay"
        className={`wizard-bg-overlay ${
          modalIsOpen ? 'wizard-bg-overlay--active' : ''
        }`}
      ></div>
      {modalIsOpen && (
        <Modal
          isOpen={modalIsOpen}
          closeModal={closeModal}
          productGalleryUrl={props.productGalleryUrl}
          designGalleryUrl={props.designGalleryUrl}
          schools={props.schools}
          handleStartOver={handleStartOver}
          handleRemoveDesign={handleRemoveDesign}
          handleRemoveProduct={handleRemoveProduct}
          handleSubmitQuoteRequest={handleSubmitQuoteRequest}
          handleChange={handleChange}
          formData={formData}
          recaptchaKey={props.recaptchaKey}
          modalStepNumber={modalStepNumber}
          setModalStepNumber={setModalStepNumber}
          previewUrl={previewUrl}
        />
      )}
      {desktopWizardButtonDomNode &&
        createPortal(
          openModalButton({
            isNew: !designRequestInProgress,
            onClick: handleOpenModal,
          }),
          desktopWizardButtonDomNode
        )}
      {mobileWizardButtonDomNode &&
        createPortal(
          openModalButton({
            isNew: !designRequestInProgress,
            onClick: handleOpenModal,
          }),
          mobileWizardButtonDomNode
        )}
      {quotePageButtonDomNode &&
        createPortal(
          openModalButton({
            isNew: !designRequestInProgress,
            onClick: handleOpenModal,
          }),
          quotePageButtonDomNode
        )}
      {addDesignDomNode &&
        createPortal(
          <AddDesignButton
            updateDesign={updateDesign}
            buttonText={addDesignButtonText}
          />,
          addDesignDomNode
        )}
      {addProductDomNode &&
        createPortal(
          <AddProductButton
            updateProduct={updateProduct}
            buttonText={addProductButtonText}
            isProductAdded={addProductButtonText === 'Product Added'}
          />,
          addProductDomNode
        )}
    </>
  );
};

export default OrderWizard;
